import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { ReactComponent as PlusIcon } from "assets/svgs/Plus.svg";
import type { NavServiceLogFilterMode } from "lib/utils/filterNavServiceLogLines";

type NavLogFilterBarProps = {
    draft: string;
    onDraftChange: (value: string) => void;
    items: string[];
    onAddItem: () => void;
    onRemoveItem: (index: number) => void;
    mode: NavServiceLogFilterMode;
    compact?: boolean;
};

/**
 * Multi-item log filter: chips above, text field + add (replaces native search clear).
 */
const NavLogFilterBar = ({
    draft,
    onDraftChange,
    items,
    onAddItem,
    onRemoveItem,
    mode,
    compact = false,
}: NavLogFilterBarProps) => {
    const tryAdd = () => {
        if (!draft.trim()) return;
        onAddItem();
    };

    const inputClass = compact
        ? "min-w-0 flex-1 border-0 bg-transparent py-1 pl-2 pr-1 text-[11px] text-white placeholder:text-black40 focus:outline-none"
        : "min-w-0 flex-1 border-0 bg-transparent py-1 pl-3 pr-1 text-h5 text-white placeholder:text-black40 focus:outline-none";

    const addButtonClass = compact
        ? "mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-black40 hover:bg-black80 hover:text-white disabled:opacity-30"
        : "mr-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-r5 text-black40 hover:bg-black80 hover:text-white disabled:opacity-30";

    return (
        <div className="min-w-0 flex-1">
            {items.length > 0 ? (
                <div className="mb-1.5 flex flex-wrap gap-1.5">
                    {items.map((item, index) => (
                        <span
                            key={`${index}-${item}`}
                            className="inline-flex max-w-full items-center gap-1 rounded-r5 border border-black70 bg-black90 py-0.5 pl-2 pr-1 text-[11px] text-white"
                        >
                            <span className="min-w-0 truncate" title={item}>
                                {item}
                            </span>
                            <button
                                type="button"
                                aria-label={`Remove filter “${item}”`}
                                title="Remove"
                                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-black40 hover:bg-black80 hover:text-white"
                                onClick={() => onRemoveItem(index)}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                <CloseIcon className="h-2.5 w-2.5" aria-hidden />
                            </button>
                        </span>
                    ))}
                </div>
            ) : null}

            <div className="flex items-center rounded-r5 border border-black70 bg-black90 focus-within:border-primary2">
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            tryAdd();
                        }
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    placeholder={
                        mode === "in" ? "Add filter-in term…" : "Add filter-out term…"
                    }
                    aria-label="Add nav.service log filter term"
                    className={inputClass}
                />
                <button
                    type="button"
                    aria-label="Add filter term"
                    title="Add"
                    disabled={!draft.trim()}
                    className={addButtonClass}
                    onClick={tryAdd}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <PlusIcon className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
                </button>
            </div>
        </div>
    );
};

export default NavLogFilterBar;
